// useSocialPosts Hook
import { useState, useEffect, useCallback } from 'react';
import { 
  SocialPost, 
  SocialAccount, 
  PublishRequest, 
  PublishResponse 
} from '../types/social.types';
import { socialMediaService } from '../services/socialMediaService';

export const useSocialPosts = () => {
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pobierz posty
  const fetchPosts = useCallback(async (filters?: Parameters<typeof socialMediaService.getPosts>[0]) => {
    setLoading(true);
    setError(null);
    try {
      const data = await socialMediaService.getPosts(filters);
      setPosts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd pobierania postów');
    } finally {
      setLoading(false);
    }
  }, []);

  // Pobierz konta
  const fetchAccounts = useCallback(async () => {
    try {
      const data = await socialMediaService.getAccounts();
      setAccounts(data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchAccounts();
  }, [fetchPosts, fetchAccounts]);

  // Utwórz post
  const createPost = async (post: Omit<SocialPost, 'id' | 'created_at'>): Promise<SocialPost> => {
    try {
      const newPost = await socialMediaService.createPost(post);
      setPosts(prev => [newPost, ...prev]);
      return newPost;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd tworzenia posta');
      throw err;
    }
  };

  // Aktualizuj post
  const updatePost = async (id: string, updates: Partial<SocialPost>): Promise<SocialPost> => {
    try {
      const updatedPost = await socialMediaService.updatePost(id, updates);
      setPosts(prev => prev.map(p => p.id === id ? updatedPost : p));
      return updatedPost;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd aktualizacji posta');
      throw err;
    }
  };

  // Usuń post
  const deletePost = async (id: string): Promise<void> => {
    try {
      await socialMediaService.deletePost(id);
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd usuwania posta');
      throw err;
    }
  };

  // Opublikuj post
  const publishPost = async (request: PublishRequest): Promise<PublishResponse> => {
    try {
      const response = await socialMediaService.publishPost(request);
      // Odśwież listę postów po publikacji
      await fetchPosts();
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd publikowania posta');
      throw err;
    }
  };

  // Zaplanuj post
  const schedulePost = async (postId: string, scheduleTime: Date): Promise<void> => {
    try {
      await socialMediaService.schedulePost(postId, scheduleTime);
      await fetchPosts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd planowania posta');
      throw err;
    }
  };

  // Pobierz zaplanowane posty
  const getScheduledPosts = async (): Promise<SocialPost[]> => {
    try {
      return await socialMediaService.getScheduledPosts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd pobierania zaplanowanych postów');
      throw err;
    }
  };

  // Dodaj konto
  const addAccount = async (account: Omit<SocialAccount, 'id'>): Promise<SocialAccount> => {
    try {
      const newAccount = await socialMediaService.addAccount(account);
      setAccounts(prev => [...prev, newAccount]);
      return newAccount;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd dodawania konta');
      throw err;
    }
  };

  // Usuń konto
  const removeAccount = async (id: string): Promise<void> => {
    try {
      await socialMediaService.removeAccount(id);
      setAccounts(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd usuwania konta');
      throw err;
    }
  };

  return {
    // Stan
    posts,
    accounts,
    loading,
    error,
    
    // Metody dla postów
    fetchPosts,
    createPost,
    updatePost,
    deletePost,
    publishPost,
    schedulePost,
    getScheduledPosts,
    
    // Metody dla kont
    addAccount,
    removeAccount,
    
    // Pomocnicze
    clearError: () => setError(null)
  };
};
